import { TrendingDown, TrendingUp } from '@mui/icons-material';
import { alpha, Box, Chip, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

export type Period = 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear';

export type PeriodSubLabel = { kind: 'month'; month: number; year: number } | { kind: 'year'; year: number };

export const currentPeriodSub = (p: Period): PeriodSubLabel => {
  const today = dayjs();
  switch (p) {
    case 'thisMonth':
      return { kind: 'month', month: today.month() + 1, year: today.year() };
    case 'lastMonth': {
      const m = today.subtract(1, 'month');
      return { kind: 'month', month: m.month() + 1, year: m.year() };
    }
    case 'thisYear':
      return { kind: 'year', year: today.year() };
    case 'lastYear':
    default:
      return { kind: 'year', year: today.subtract(1, 'year').year() };
  }
};

export const previousPeriodSub = (p: Period): PeriodSubLabel => {
  const today = dayjs();
  switch (p) {
    case 'thisMonth': {
      const m = today.subtract(1, 'month');
      return { kind: 'month', month: m.month() + 1, year: m.year() };
    }
    case 'lastMonth': {
      const m = today.subtract(2, 'month');
      return { kind: 'month', month: m.month() + 1, year: m.year() };
    }
    case 'thisYear':
      return { kind: 'year', year: today.subtract(1, 'year').year() };
    case 'lastYear':
    default:
      return { kind: 'year', year: today.subtract(2, 'year').year() };
  }
};

export const periodRange = (p: Period): { from: string; to: string } => {
  const today = dayjs();
  switch (p) {
    case 'lastMonth':
      return {
        from: today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        to: today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case 'thisYear':
      return { from: today.startOf('year').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'lastYear':
      return {
        from: today.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        to: today.subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
      };
    case 'thisMonth':
    default:
      return { from: today.startOf('month').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
  }
};

/** Returnerar samma längd, en period bakåt — för trend-jämförelse */
export const previousPeriodRange = (p: Period): { from: string; to: string } => {
  const today = dayjs();
  switch (p) {
    case 'lastMonth':
      return {
        from: today.subtract(2, 'month').startOf('month').format('YYYY-MM-DD'),
        to: today.subtract(2, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case 'thisYear':
      return {
        from: today.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        to: today.subtract(1, 'year').format('YYYY-MM-DD'),
      };
    case 'lastYear':
      return {
        from: today.subtract(2, 'year').startOf('year').format('YYYY-MM-DD'),
        to: today.subtract(2, 'year').endOf('year').format('YYYY-MM-DD'),
      };
    case 'thisMonth':
    default:
      return {
        from: today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        to: today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
  }
};

type TrendChipProps = {
  current: number;
  previous: number;
  period: Period;
};

type TrendBadgeProps = {
  current: number;
  previous: number;
};

export const TrendBadge = ({ current, previous }: TrendBadgeProps) => {
  if (previous === 0) return null;
  const pct = Math.round(((current - previous) / previous) * 1000) / 10;
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <Chip
      size='small'
      icon={<Icon sx={{ fontSize: 14 }} />}
      label={`${pct > 0 ? '+' : ''}${pct}%`}
      sx={{
        bgcolor: (theme) => alpha(positive ? theme.palette.success.main : theme.palette.error.main, 0.12),
        color: positive ? 'success.dark' : 'error.dark',
        fontWeight: 700,
        height: 22,
        '& .MuiChip-icon': { ml: 0.75, color: 'inherit' },
      }}
    />
  );
};

const formatSub = (sub: PeriodSubLabel, t: (key: string) => string): string =>
  sub.kind === 'month' ? `${t(`dashboard:period.months.${sub.month}`)} ${sub.year}` : `${sub.year}`;

export const formatPeriodSubLabel = formatSub;

export const TrendChip = ({ current, previous, period }: TrendChipProps) => {
  const { t } = useTranslation(['dashboard']);
  if (previous === 0) return null;
  const tooltip = t('dashboard:period.trendVsTooltip', {
    period: t(`dashboard:period.previousLabels.${period}`),
    date: formatSub(previousPeriodSub(period), t),
  });
  return (
    <Tooltip title={tooltip} arrow>
      <Box sx={{ display: 'inline-flex', cursor: 'default' }}>
        <TrendBadge current={current} previous={previous} />
      </Box>
    </Tooltip>
  );
};
